import type { ColumnType } from "kysely";
export type Generated<T> = T extends ColumnType<infer S, infer I, infer U>
  ? ColumnType<S, I | undefined, U>
  : ColumnType<T, T | undefined, T>;
export type Timestamp = ColumnType<Date, Date | string, Date | string>;

export const AttachmentChildType = {
    File: "File",
    Comment: "Comment",
    FileRevision: "FileRevision",
    Project: "Project"
} as const;
export type AttachmentChildType = (typeof AttachmentChildType)[keyof typeof AttachmentChildType];
export const AttachmentParentType = {
    File: "File",
    Project: "Project",
    Comment: "Comment",
    ProjectRevision: "ProjectRevision",
    Workspace: "Workspace"
} as const;
export type AttachmentParentType = (typeof AttachmentParentType)[keyof typeof AttachmentParentType];
export const UserPreferenceType = {
    theme: "theme"
} as const;
export type UserPreferenceType = (typeof UserPreferenceType)[keyof typeof UserPreferenceType];
export const WorkspaceType = {
    LOCAL: "LOCAL",
    PERSONAL: "PERSONAL",
    SHARED: "SHARED"
} as const;
export type WorkspaceType = (typeof WorkspaceType)[keyof typeof WorkspaceType];
export const ProjectType = {
    DEFAULT: "DEFAULT",
    SONG: "SONG"
} as const;
export type ProjectType = (typeof ProjectType)[keyof typeof ProjectType];
export const SystemType = {
    local: "local",
    remote: "remote"
} as const;
export type SystemType = (typeof SystemType)[keyof typeof SystemType];
export const VersioningMode = {
    NONE: "NONE",
    AUTO: "AUTO",
    ATTACH: "ATTACH"
} as const;
export type VersioningMode = (typeof VersioningMode)[keyof typeof VersioningMode];
export const AttributeName = {
    description: "description",
    chord_lyrics: "chord_lyrics",
    waveform_data: "waveform_data",
    duration: "duration",
    launcher_file: "launcher_file",
    bounce_file: "bounce_file"
} as const;
export type AttributeName = (typeof AttributeName)[keyof typeof AttributeName];
export const AttributeParentType = {
    File: "File",
    Project: "Project"
} as const;
export type AttributeParentType = (typeof AttributeParentType)[keyof typeof AttributeParentType];
export const StarParentType = {
    File: "File",
    Project: "Project"
} as const;
export type StarParentType = (typeof StarParentType)[keyof typeof StarParentType];
export const UserActivityObjectType = {
    File: "File",
    Project: "Project",
    Comment: "Comment"
} as const;
export type UserActivityObjectType = (typeof UserActivityObjectType)[keyof typeof UserActivityObjectType];
export const UserActivityName = {
    LISTEN: "LISTEN",
    COMMENT: "COMMENT",
    UPDATE_LYRICS: "UPDATE_LYRICS",
    UPDATE_DESCRIPTION: "UPDATE_DESCRIPTION",
    UPLOAD_MANUAL: "UPLOAD_MANUAL",
    UPLOAD_AUTO: "UPLOAD_AUTO"
} as const;
export type UserActivityName = (typeof UserActivityName)[keyof typeof UserActivityName];
export type Attachment = {
    id: string;
    child_type: AttachmentChildType;
    child_id: string;
    parent_type: AttachmentParentType;
    parent_id: string;
    created_by: string;
    created_at: Generated<string>;
    updated_at: string;
    workspace_id: string;
    index: number | null;
    hidden_at: string | null;
};
export type Attribute = {
    id: string;
    parent_type: AttributeParentType;
    parent_id: string;
    name: AttributeName;
    value: string;
    workspace_id: string;
    created_by: string;
    created_at: Generated<string>;
    updated_at: Generated<string>;
};
export type AttributeRevision = {
    id: string;
    attribute_id: string;
    revision_num: number;
    parent_revision_type: string;
    parent_revision_id: string;
    workspace_id: string;
    value: string;
    created_by: string;
    created_at: Generated<string>;
    projectRevisionId: string | null;
};
export type Comment = {
    id: string;
    text: string;
    reply_parent_id: string | null;
    workspace_id: string;
    created_by: string;
    created_at: Generated<string>;
    updated_at: Generated<string>;
};
export type File = {
    id: string;
    name: string;
    relative_path: string;
    mime_type: string;
    size: number;
    earliest_known_modified_at: string | null;
    workspace_id: string;
    created_by: string;
    created_at: Generated<string>;
    updated_at: Generated<string>;
    deleted_at: string | null;
    versioning_mode: Generated<VersioningMode>;
    auto_version: Generated<number>;
};
export type FileRevision = {
    id: string;
    revision_num: number;
    revision_name: string;
    file_id: string;
    created_by: string;
    created_at: Generated<string>;
    deleted_at: string | null;
    workspace_id: string;
};
export type Project = {
    id: string;
    name: string;
    description: string | null;
    workspace_id: string;
    type: ProjectType;
    created_by: string;
    created_at: Generated<string>;
    updated_at: Generated<string>;
    parent_id: string | null;
    project_ancestry: string | null;
};
export type ProjectRevision = {
    id: string;
    project_id: string;
    workspace_id: string;
    revision_num: number;
    revision_name: string;
    manual: Generated<number>;
    created_by: string;
    created_at: Generated<string>;
};
export type RawFile = {
    id: string;
    file_id: string;
    full_path: string;
    workspace_id: string;
    created_by: string;
    created_at: Generated<string>;
    updated_at: Generated<string>;
    last_modified_at: string | null;
    file_revision_id: string;
    size: number | null;
    mime_type: string | null;
    hash: string | null;
    system: SystemType | null;
};
export type RawProject = {
    id: string;
    workspace_id: string;
    full_path: string;
    project_id: string;
    system: SystemType;
    created_by: string;
    created_at: Generated<string>;
    updated_at: Generated<string>;
};
export type RawWorkspace = {
    id: string;
    workspace_id: string;
    full_path: string;
    system: string;
    created_by: string;
    created_at: Generated<string>;
    updated_at: Generated<string>;
};
export type Star = {
    id: string;
    workspace_id: string;
    created_by: string;
    parent_type: StarParentType;
    parent_id: string;
};
export type User = {
    id: string;
    user_auth_id: string | null;
    name: string;
    created_at: Generated<string>;
    updated_at: Generated<string>;
};
export type UserActivity = {
    id: string;
    activity_name: UserActivityName;
    object_type: UserActivityObjectType;
    object_id: string;
    workspace_id: string;
    created_by: string;
    created_at: Generated<string>;
    updated_at: Generated<string>;
};
export type UserPreference = {
    id: string;
    user_id: string;
    workspace_id: string | null;
    key: UserPreferenceType;
    value: string;
    created_by: string;
    created_at: Generated<string>;
    updated_at: Generated<string>;
};
export type Workspace = {
    id: string;
    name: string;
    owner_id: string;
    created_by: string;
    created_at: Generated<string>;
    updated_at: Generated<string>;
    type: WorkspaceType;
};
export type WorkspaceMember = {
    id: string;
    workspace_id: string;
    user_id: string;
    role: Generated<string>;
    created_by: string;
    created_at: Generated<string>;
};
export type DB = {
    Attachment: Attachment;
    Attribute: Attribute;
    AttributeRevision: AttributeRevision;
    Comment: Comment;
    File: File;
    FileRevision: FileRevision;
    Project: Project;
    ProjectRevision: ProjectRevision;
    RawFile: RawFile;
    RawProject: RawProject;
    RawWorkspace: RawWorkspace;
    Star: Star;
    User: User;
    UserActivity: UserActivity;
    UserPreference: UserPreference;
    Workspace: Workspace;
    WorkspaceMember: WorkspaceMember;
};
